import toast, { Toaster } from 'react-hot-toast';
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Store, Truck, ShieldCheck, Mail, Smartphone } from 'lucide-react';
import api from '../../utils/api';

const Login = () => {
  const navigate = useNavigate();
  const { login: contextLogin } = useAuth(); // Use the login function from context

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [otp, setOtp] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('****');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [specialState, setSpecialState] = useState(null);

  const SUPPORT_EMAIL = 'hello@tikhat.in';
  const SUPPORT_PHONE_1 = '+91 91403 17009';
  const SUPPORT_PHONE_2 = '+91 88407 37660';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSpecialState(null);
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSpecialState(null);

    try {
      console.log('📤 Sending OTP request:', { email: formData.email });
      
      const res = await api.post('/auth/send-login-otp', {
        email: formData.email,
        password: formData.password,
      });

      console.log('📥 OTP Response:', res.data);

      if (res.data?.success) {
        setMaskedPhone(res.data.maskedPhone || '****');
        toast.success(res.data.message || 'OTP sent successfully');
        setStep(2);
      } else {
        setError('Unable to send OTP');
      }
    } catch (err) {
      console.error('❌ OTP Send Error:', err);
      const errMsg = err?.response?.data?.detail || 'Login failed';
      const errLower = errMsg.toLowerCase();

      if (errLower.includes('pending')) {
        setSpecialState({
          type: 'pending',
          note: 'Your partner account is under verification.',
        });
        toast.error('Account pending verification');
      } else if (errLower.includes('frozen')) {
        setSpecialState({
          type: 'frozen',
          note: 'Your partner account is temporarily blocked.',
        });
        toast.error('Account frozen');
      } else {
        setError(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setLoading(false);
      return;
    }

    try {
      console.log('🔐 Verifying OTP:', { 
        email: formData.email, 
        otp: otp,
        otpLength: otp.length 
      });

      const res = await api.post('/auth/verify-login-otp', {
        email: formData.email,
        otp: otp,
      });

      console.log('✅ Verify Response:', res.data);

      if (res.data?.user && res.data?.access_token) {
        // Store in localStorage
        localStorage.setItem('access_token', res.data.access_token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        
        // Force page reload to update auth context
        toast.success('Login successful!');
        
        // Small delay to show toast
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 500);
      } else {
        console.warn('⚠️ Missing user or token in response');
        setError('Login failed. Please try again.');
      }
    } catch (err) {
      console.error('❌ OTP Verify Error:', err);
      console.error('Error response:', err?.response?.data);
      setError(err?.response?.data?.detail || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    setError('');

    try {
      const res = await api.post('/auth/send-login-otp', {
        email: formData.email,
        password: formData.password,
      });

      toast.success(res.data?.message || 'OTP resent successfully');
      setMaskedPhone(res.data.maskedPhone || '****');
      setOtp(''); // Clear OTP input
    } catch {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex">
      <Toaster position="top-right" />

      {/* LEFT – BRAND / INFO */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-light via-white to-success-light p-12 flex-col justify-center">
        <div className="max-w-lg">
          <div className="flex items-center gap-3 mb-8">
            <Store className="w-10 h-10 text-primary" />
            <h1 className="text-4xl font-heading font-bold text-primary">
              Tikhat Foods Partner
            </h1>
          </div>

          <h2 className="text-3xl font-heading font-bold text-gray-800 mb-6">
            Powering Local Grocery Businesses
          </h2>

          <p className="text-lg text-gray-700 mb-8">
            Join Tikhat Foods as an authorised partner and take part in our
            grocery distribution and production ecosystem.
          </p>

          <div className="grid grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm mb-2">
                <Store className="w-8 h-8 text-primary mx-auto" />
              </div>
              <p className="text-sm font-semibold text-gray-700">Partner Model</p>
              <p className="text-xs text-gray-600">Business Oriented</p>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm mb-2">
                <Truck className="w-8 h-8 text-success mx-auto" />
              </div>
              <p className="text-sm font-semibold text-gray-700">Supply Network</p>
              <p className="text-xs text-gray-600">Pan-India Reach</p>
            </div>

            <div className="text-center">
              <div className="bg-white rounded-lg p-4 shadow-sm mb-2">
                <ShieldCheck className="w-8 h-8 text-warning mx-auto" />
              </div>
              <p className="text-sm font-semibold text-gray-700">Verified</p>
              <p className="text-xs text-gray-600">KYC Enabled</p>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT – LOGIN */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">

          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <img
              src="/logo.png"
              alt="Tikhat Partner"
              className="h-24 object-contain"
            />
          </div>

          <div className="bg-white rounded-lg shadow-md p-8">

            {/* ---------------- STEP 1: EMAIL + PASSWORD ---------------- */}
            {step === 1 && (
              <>
                <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
                  Partner Login
                </h2>
                <p className="text-gray-600 mb-6">
                  Access your Tikhat Foods partner dashboard
                </p>

                {specialState?.type && (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-4 rounded-md mb-4">
                    <p className="font-semibold">
                      {specialState.type === 'pending'
                        ? 'Verification Pending'
                        : 'Account Restricted'}
                    </p>
                    <p className="text-sm mt-1">{specialState.note}</p>

                    <div className="mt-3 text-sm">
                      <p>Contact Tikhat Foods Support:</p>
                      <p>📞 {SUPPORT_PHONE_1}</p>
                      <p>📞 {SUPPORT_PHONE_2}</p>
                      <p>📧 {SUPPORT_EMAIL}</p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-error-light border border-error text-error-dark px-4 py-3 rounded-md mb-4">
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSendOTP} className="space-y-4">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email Address"
                    className="w-full px-4 py-3 border rounded-md"
                  />

                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="Password"
                    className="w-full px-4 py-3 border rounded-md"
                  />

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-primary text-white font-semibold py-3 rounded-md hover:bg-primary-dark transition disabled:opacity-50"
                  >
                    {loading ? 'Sending OTP...' : 'Login'}
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <Link to="/forgot-password" className="text-sm text-primary font-semibold hover:underline">
                    Forgot Password?
                  </Link>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-600">
                    Not a partner yet?{' '}
                    <Link to="/register" className="text-primary font-semibold hover:underline">
                      Register here
                    </Link>
                  </p>
                </div>
              </>
            )}

            {/* ---------------- STEP 2: VERIFY OTP ---------------- */}
            {step === 2 && (
              <>
                <button
                  onClick={() => {
                    setStep(1);
                    setOtp('');
                    setError('');
                  }}
                  className="flex items-center text-primary mb-4 text-sm hover:underline"
                >
                  ← Back
                </button>

                <h2 className="text-2xl font-bold mb-2">Verify OTP</h2>

                <div className="bg-blue-50 border rounded-md p-4 mb-4">
                  <div className="flex items-center text-sm mb-1">
                    <Mail className="w-4 h-4 mr-2" />
                    {formData.email}
                  </div>
                  <div className="flex items-center text-sm">
                    <Smartphone className="w-4 h-4 mr-2" />
                    +91 {maskedPhone}
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-4">
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <div>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtp(value);
                        console.log('OTP Input:', value);
                      }}
                      placeholder="Enter 6-digit OTP"
                      className="w-full px-4 py-3 border rounded-md text-center text-xl tracking-widest focus:ring-2 focus:ring-primary focus:border-primary"
                      required
                      autoFocus
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Check your email and SMS for the OTP
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full bg-primary text-white py-3 rounded-md hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? 'Verifying...' : 'Verify & Login'}
                  </button>

                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="w-full text-sm text-primary mt-2 hover:underline disabled:opacity-50"
                  >
                    Resend OTP
                  </button>
                </form>
              </>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;